import React from "react";
import { FaEdit, FaTrash } from "react-icons/fa";

import {
  getFullName,
  getRoleDisplay,
  getSectionName,
  getUserId,
  resolveMediaUrl,
} from "../utils/usersManagerUtils";

export default function UsersTable({
  users,
  sections,
  loading,
  totalRows,
  visibleCount,
  demoMode,
  onLoadMore,
  onEdit,
  onDelete,
}) {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="border-b border-slate-200 p-4 dark:border-white/10 sm:p-5 lg:p-6">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
          Tableau des utilisateurs
        </h2>

        <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
          {totalRows.toLocaleString("fr-FR")} utilisateur
          {totalRows > 1 ? "s" : ""} trouvé{totalRows > 1 ? "s" : ""}.
        </p>
      </div>

      <div className="p-3 sm:p-5 lg:p-6">
        <div className="overflow-auto rounded-[24px] border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950/60">
          <table className="min-w-[1080px] w-full text-sm">
            <thead className="sticky top-0 z-30 bg-slate-50 dark:bg-slate-900">
              <tr className="text-left">
                <th className="sticky left-0 z-40 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  Photo
                </th>

                <th className="sticky left-[82px] z-40 bg-slate-50 px-4 py-3 font-bold text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                  Utilisateur
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Email
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Rôle
                </th>

                <th className="px-4 py-3 font-bold text-slate-700 dark:text-slate-200">
                  Section
                </th>

                <th className="px-4 py-3 text-right font-bold text-slate-700 dark:text-slate-200">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 dark:divide-white/10">
              {loading ? (
                [...Array(8)].map((_, index) => (
                  <tr key={index}>
                    <td colSpan={6} className="px-4 py-3">
                      <div className="h-10 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
                    </td>
                  </tr>
                ))
              ) : users.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-10 text-center text-slate-500 dark:text-slate-400"
                  >
                    Aucun utilisateur ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const userId = getUserId(user);
                  const role = getRoleDisplay(user.role_id);

                  return (
                    <tr
                      key={userId}
                      className="transition hover:bg-slate-50 dark:hover:bg-slate-900"
                    >
                      <td className="sticky left-0 z-20 bg-white px-4 py-3 dark:bg-slate-950">
                        <img
                          src={resolveMediaUrl(
                            user.photoURL,
                            "/images/user_avatar.jpg",
                          )}
                          alt={getFullName(user)}
                          className="h-11 w-11 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                          onError={(event) => {
                            event.currentTarget.src = "/images/user_avatar.jpg";
                          }}
                        />
                      </td>

                      <td className="sticky left-[82px] z-20 bg-white px-4 py-3 dark:bg-slate-950">
                        <div className="font-bold text-slate-900 dark:text-white">
                          {getFullName(user)}
                        </div>

                        <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                          ID {userId}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        {user.email || "—"}
                      </td>

                      <td className="px-4 py-3">
                        <span
                          className={[
                            "inline-flex rounded-full px-3 py-1 text-xs font-bold ring-1",
                            role.className,
                          ].join(" ")}
                        >
                          {role.label}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-slate-700 dark:text-slate-300">
                        {getSectionName(sections, user.section_id)}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => onEdit(user)}
                            disabled={demoMode}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
                            title="Modifier"
                          >
                            <FaEdit />
                          </button>

                          <button
                            type="button"
                            onClick={() => onDelete(user)}
                            disabled={demoMode}
                            className="flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                            title="Supprimer"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {!loading && visibleCount < totalRows && (
          <div className="mt-4 flex justify-center">
            <button
              type="button"
              onClick={onLoadMore}
              className="rounded-2xl border border-slate-200 bg-white px-5 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-white/10"
            >
              Afficher plus ({visibleCount} / {totalRows})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
